import { Alert, Box, useTheme } from "@mui/material";

import { useAuth } from "@/providers";
import { DragAndDropFiles } from "@/shared/components/DragAndDropFiles";
import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import { FileSystemNode } from "@/shared/domain/files/filesystem";

import styles from "./AssociationFileBrowser.module.css";
import { FileBrowserDisplays } from "./FileBrowserDisplays";
import { useInheritedFiles } from "./inheritedFiles";
import { PaginationConfig } from "./state";

interface DragAndDropDisplayProps {
  currentPage: FileSystemNode[] | undefined;
  associationId: string;
  displayMode: string;
  error: Error | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  filePrefix: string | undefined;
  gridItems: number;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  isFetching: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  currentPageFetchedAt: number;
  isSearching: boolean;
  onClearError: () => void;
  onChangeInput: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onDropComplete: (error?: Error) => Promise<void>;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined,
  ) => void;
  selectedItems: FileSystemNode[];
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
  tableConfig: PaginationConfig;
  totalItemCount: number | undefined;
  hasNextPage: boolean;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional — absent when `release-3d-assets` is off, and callers pass it
   * straight through, so the components that act on it handle its absence.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  /** Whether files show their ingestion labels; passed straight through. */
  showIngestionLabels?: boolean;
}

export const DragAndDropDisplay: React.FC<DragAndDropDisplayProps> = ({
  currentPage,
  associationId,
  displayMode,
  error,
  fileInputRef,
  filePrefix,
  gridItems,
  handleDeleteRow,
  handleRowDoubleClick,
  handleSelectRows,
  isFetching,
  isPending,
  isPlaceholderData,
  currentPageFetchedAt,
  isSearching,
  onClearError,
  onChangeInput,
  onDropComplete,
  onPageChange,
  onRowsPerPageChange,
  selectedItems,
  sortColumn,
  sortDirection,
  onSort,
  tableConfig,
  totalItemCount,
  hasNextPage,
  handleActionConfirmation,
  onIngestAsAsset,
  showIngestionLabels,
}) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();
  // Inherited files take no uploads, so they are shown without a drop zone.
  const readOnly = useInheritedFiles() !== null;

  let dragAndDropContent;

  const displays = (
    <FileBrowserDisplays
      currentPage={currentPage}
      displayMode={displayMode}
      associationId={associationId}
      gridItems={gridItems}
      handleDeleteRow={handleDeleteRow}
      handleRowDoubleClick={handleRowDoubleClick}
      isFetching={isFetching}
      isPending={isPending}
      isPlaceholderData={isPlaceholderData}
      currentPageFetchedAt={currentPageFetchedAt}
      isSearching={isSearching}
      onPageChange={(event, newPage) => {
        onPageChange(event, newPage);
      }}
      onRowsPerPageChange={(event) => {
        onRowsPerPageChange(event);
      }}
      selectedItems={selectedItems}
      handleSelectRows={handleSelectRows}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
      tableConfig={tableConfig}
      totalItemCount={totalItemCount}
      hasNextPage={hasNextPage}
      handleActionConfirmation={handleActionConfirmation}
      onIngestAsAsset={onIngestAsAsset}
      showIngestionLabels={showIngestionLabels}
    />
  );

  if (error) {
    dragAndDropContent = (
      <Box sx={{ mt: theme.spacing(2) }}>
        <Alert onClose={onClearError} severity="error">
          {error.message}
        </Alert>
      </Box>
    );
  } else if (readOnly) {
    dragAndDropContent = (
      <div className={styles.readOnlyDisplays}>{displays}</div>
    );
  } else {
    dragAndDropContent = (
      <DragAndDropFiles
        containerStyle={{
          marginTop: theme.spacing(1),
          backgroundColor: "transparent",
        }}
        displayOverlayOnFileHover={true}
        orgId={currentOrganization?.org_id ?? ""}
        associationId={associationId}
        onDropComplete={onDropComplete}
        filePrefix={filePrefix}
      >
        {displays}

        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            void onChangeInput(e);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
        />
      </DragAndDropFiles>
    );
  }

  return dragAndDropContent;
};
